import {SelectPanel, type SelectPanelProps} from '@primer/react'
import {useId, useState} from 'react'
import {useNestableActionMenu} from './NestableActionMenuContext'

/** Unlike PRC `SelectPanel`, the nested one always controls its own visibility. */
export type NestedSelectPanelProps = Omit<SelectPanelProps, 'open' | 'onOpenChange' | 'id'> & {
  /**
   * Optional callback to be fired when the nested select panel opens or closes.
   */
  onOpenChange?: SelectPanelProps['onOpenChange']
}

/**
 * `SelectPanel` for use inside a `NestableActionMenu`. The most notable differences are that it controls its own
 * visibility and it will automatically close the entire menu stack when selection completes.
 *
 * The `renderAnchor` prop should typically spread props to and return `NestableActionMenu.MenuItemAnchor`.
 */
export function NestedSelectPanel({
  overlayProps,
  onOpenChange: externalOnOpenChange,
  ...props
}: NestedSelectPanelProps) {
  const context = useNestableActionMenu()
  const id = useId()
  const [open, setOpen] = useState(false)

  /** Close the entire stack when selection is confirmed. */
  const onOpenChange: SelectPanelProps['onOpenChange'] = (newOpen, gesture) => {
    externalOnOpenChange?.(newOpen, gesture)
    setOpen(newOpen)

    // Why can't ActionMenu be this easy??
    if (!newOpen && gesture === 'selection') context?.onCloseStack()

    context?.setNestedSelectPanelOpenState(id, newOpen)
  }

  return (
    <SelectPanel
      // Transforming the prop type with Omit seems to break the union type when passed into `SelectPanel`
      {...(props as SelectPanelProps)}
      open={open}
      id={id}
      onOpenChange={onOpenChange}
      overlayProps={{
        anchorSide: 'outside-right',
        ...overlayProps,
      }}
    />
  )
}
NestedSelectPanel.displayName = 'NestableActionMenu.NestedSelectPanel'
